import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider } from "@material-ui/core";
import { showModal } from "../../../actions/actionCreators";
import LocalNavMenu from "../LocalNavMenu";
import AdminRoleInfo from "./AdminRoleInfo";
import AdminGlobalLinks from "./AdminGlobalLinks";
import CompanyFeatureAccessSection from "./CompanyFeatureAccessSection";
import CompanyFieldManagementSection from "./CompanyFieldManagementSection";
import SuperAdminExtrasSection from "./SuperAdminExtrasSection";
import BrandingAndSystemSection from "./BrandingAndSystemSection";

// Local pure helper (kept in-file to avoid extra import noise)
const isAdminMenuActive = (pathname, extra = []) => {
    const adminPaths = ["/ClientAccess/SingleViewControlCenter", "/Field", "/admin/cached-data", ...extra];
    return pathname.includes("/admin/") || adminPaths.some(p => pathname.includes(p));
};

export default function CombinedAdminMenu() {
    const dispatch = useDispatch();
    const stateSlice = useSelector(state => ({
        pathname: state.router.location.pathname,
        roles: state.session.roles,
        companyId: state.session.companyId,
        isCompanyHasManageAccess: state.session.isCompanyHasManageAccess,
        enabledFeatures: state.session.enabledFeatures,
        userOnlyFeatures: state.session.userOnlyFeatures,
    }));

    const handlers = useMemo(
        () => ({
            openManageFeatures: () => dispatch(showModal("MANAGE_FEATURES", null)),
            handleFieldLabels: () => dispatch(showModal("EDIT_FIELD_LABELS")),
            handleCloneCompanyLabels: () => dispatch(showModal("CLONE_COMPANY_LABELS")),
        }),
        [dispatch]
    );

    const flags = useMemo(() => {
        const roleSet = new Set(stateSlice.roles || []);
        const featureSet = new Set(stateSlice.enabledFeatures || []);
        const userOnlySet = new Set(stateSlice.userOnlyFeatures || []);
        const hasSuperAdmin = roleSet.has("Super Admin");
        const hasExperianAdmin = roleSet.has("Experian Admin");
        const hasCompanySelected = stateSlice.companyId != null && stateSlice.companyId > 0;
        return {
            hasSuperAdmin,
            hasExperianAdmin,
            hasCompanySelected,
            canSeeMenu: hasSuperAdmin || hasExperianAdmin,
            hasVisibilityForCompany: featureSet.has("import-export-field-visibility"),
            hastEditFieldRatesFeature: userOnlySet.has("edit-field-rates"),
            isActive: isAdminMenuActive(stateSlice.pathname),
        };
    }, [stateSlice]);

    if (!flags.canSeeMenu) return null;

    // Build logical section blocks then interleave dividers automatically
    const sectionBlocks = [
        {
            key: "role-global",
            show: true,
            node: (
                <>
                    <AdminRoleInfo hasSuperAdmin={flags.hasSuperAdmin} />
                    <AdminGlobalLinks
                        hasSuperAdmin={flags.hasSuperAdmin}
                        hasExperianAdmin={flags.hasExperianAdmin}
                        hasCompanySelected={flags.hasCompanySelected}
                    />
                </>
            ),
        },
        {
            key: "feature-access",
            show: flags.hasCompanySelected,
            node: (
                <CompanyFeatureAccessSection
                    hasCompanySelected={flags.hasCompanySelected}
                    hasSuperAdmin={flags.hasSuperAdmin}
                    hasExperianAdmin={flags.hasExperianAdmin}
                    onOpenManageFeatures={handlers.openManageFeatures}
                />
            ),
        },
        {
            key: "field-mgmt",
            show: flags.hasCompanySelected,
            node: (
                <CompanyFieldManagementSection
                    hasCompanySelected={flags.hasCompanySelected}
                    isCompanyHasManageAccess={stateSlice.isCompanyHasManageAccess}
                    hasSuperAdmin={flags.hasSuperAdmin}
                    hasVisibilityForCompany={flags.hasVisibilityForCompany}
                    hastEditFieldRatesFeature={flags.hastEditFieldRatesFeature}
                    onEditFieldLabels={handlers.handleFieldLabels}
                    onCloneFieldLabels={handlers.handleCloneCompanyLabels}
                />
            ),
        },
        {
            key: "super-admin-extras",
            show: true,
            node: (
                <SuperAdminExtrasSection
                    hasSuperAdmin={flags.hasSuperAdmin}
                    hasExperianAdmin={flags.hasExperianAdmin}
                />
            ),
        },
        {
            key: "branding-system",
            show: true,
            node: (
                <BrandingAndSystemSection
                    hasSuperAdmin={flags.hasSuperAdmin}
                    hasExperianAdmin={flags.hasExperianAdmin}
                />
            ),
        },
    ].filter(b => b.show);

    const interleaved = [];
    sectionBlocks.forEach((block, idx) => {
        if (idx > 0) interleaved.push(<Divider key={`div-${idx}`} name="divider" />);
        interleaved.push(<React.Fragment key={block.key}>{block.node}</React.Fragment>);
    });

    return (
        <li id="admin" className={`menu-li ${flags.isActive ? "active" : ""}`}>
            <LocalNavMenu
                id="combined-admin"
                key="combinedadmin"
                text="Admin"
                isCollapsed
                collapsedIcon="/Images/Icons/certified.png"
            >
                {interleaved}
            </LocalNavMenu>
        </li>
    );
}
